import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Divider,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchOperatorById,
  fetchSupervisors,
  createOperator,
  updateOperatorAction,
  setSelectedOperator,
  setValidationErrors,
} from '../../store/reducers/operatorsSlice';
import { CreateOperatorRequest, UpdateOperatorRequest } from '../../services/operatorsApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  form: {
    '& .MuiTextField-root': {
      marginBottom: theme.spacing(2),
    },
    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(2),
    },
  },
  buttonGroup: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
}));

const OperatorsEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const isEditing = Boolean(id);
  const operatorId = id ? parseInt(id, 10) : null;

  const {
    selectedOperator,
    supervisors,
    loading,
    error,
    validationErrors,
  } = useSelector((state: RootState) => state.operators);

  // Form state
  const [formData, setFormData] = useState({
    Name: '',
    EmployeeNumber: '',
    SupervisorId: null as number | null,
    Active: true,
  });

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchSupervisors());
    
    if (isEditing && operatorId) {
      dispatch(fetchOperatorById(operatorId));
    } else {
      dispatch(setSelectedOperator(null));
    }

    // Clear validation errors
    dispatch(setValidationErrors([]));
  }, [dispatch, isEditing, operatorId]);

  // Update form data when selectedOperator changes
  useEffect(() => {
    if (isEditing && selectedOperator) {
      setFormData({
        Name: selectedOperator.Name || '',
        EmployeeNumber: selectedOperator.EmployeeNumber || '',
        SupervisorId: selectedOperator.SupervisorId || null,
        Active: selectedOperator.Active ?? true,
      });
    } else if (!isEditing) {
      setFormData({
        Name: '',
        EmployeeNumber: '',
        SupervisorId: null,
        Active: true,
      });
    }
  }, [selectedOperator, isEditing]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      dispatch(setValidationErrors([]));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (isEditing && operatorId) {
      const updateData: UpdateOperatorRequest = {
        Id: operatorId,
        ...formData,
      };
      dispatch(updateOperatorAction(updateData));
    } else {
      const createData: CreateOperatorRequest = {
        ...formData,
      };
      dispatch(createOperator(createData));
    }
  };

  const handleCancel = () => {
    navigate('/operators');
  };

  const handleBack = () => {
    navigate('/operators');
  };

  if (loading && isEditing && !selectedOperator) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper className={classes.paper}>
        {/* Header */}
        <Box className={classes.header}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBack}
            style={{ marginBottom: 16 }}
          >
            Back to Operators
          </Button>
          <Typography variant="h4">
            {isEditing ? 'Edit Operator' : 'Add New Operator'}
          </Typography>
          {isEditing && selectedOperator && (
            <Typography variant="subtitle1" color="textSecondary">
              Employee Number: {selectedOperator.EmployeeNumber}
            </Typography>
          )}
        </Box>

        <Divider style={{ marginBottom: 24 }} />

        {/* Form */}
        <form onSubmit={handleSubmit} className={classes.form}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                variant="outlined"
                value={formData.Name}
                onChange={(e) => handleInputChange('Name', e.target.value)}
                placeholder="Enter operator name"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Employee Number"
                variant="outlined"
                value={formData.EmployeeNumber}
                onChange={(e) => handleInputChange('EmployeeNumber', e.target.value)}
                placeholder="Enter employee number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Supervisor</InputLabel>
                <Select
                  value={formData.SupervisorId || ''}
                  onChange={(e) => handleInputChange('SupervisorId', e.target.value || null)}
                  label="Supervisor"
                >
                  <MenuItem value="">
                    <em>No Supervisor</em>
                  </MenuItem>
                  {supervisors.map((supervisor) => (
                    <MenuItem key={supervisor.Id} value={supervisor.Id}>
                      {supervisor.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active}
                    onChange={(e) => handleInputChange('Active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          {/* Button Group */}
          <Box className={classes.buttonGroup}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} style={{ marginRight: 8 }} />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Update Operator' : 'Create Operator'
              )}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default OperatorsEdit; 